import { useState, useRef, useEffect } from "react";
import { ScanText, Upload, Type, Sparkles, CalendarClock, ListTodo, BookOpen, AlertTriangle, X, FileText, History, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { Loader, Bullets, SpeakButton, SectionCard } from "@/components/common";
import { speak } from "@/lib/voice";

const ACTIONS = [
  { key: "explain", label: "Explain it" },
  { key: "simplify", label: "Make it simple" },
  { key: "summarize", label: "Summarise" },
  { key: "extract_tasks", label: "What must I do?" },
  { key: "translate", label: "Translate" },
];

function downscale(file, max = 1024, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > height && width > max) { height = (height * max) / width; width = max; }
      else if (height > max) { width = (width * max) / height; height = max; }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality).split(",")[1]);
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default function ReadThis() {
  const { mode, isSenior } = useAppMode();
  const [tab, setTab] = useState("image");
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [text, setText] = useState("");
  const [pdfB64, setPdfB64] = useState(null);
  const [pdfName, setPdfName] = useState(null);
  const [action, setAction] = useState("explain");
  const [lang, setLang] = useState("Spanish");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);

  const loadDocs = () => api.get("/documents").then((r) => setDocs(r.data.items)).catch(() => {});
  useEffect(() => { loadDocs(); }, []);
  const delDoc = async (id) => { await api.delete(`/documents/${id}`); loadDocs(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(jpeg|png|webp|jpg)/.test(f.type)) { toast.error("Please choose a photo (JPG, PNG or WEBP)."); return; }
    setPreview(URL.createObjectURL(f));
    try { setB64(await downscale(f)); } catch { toast.error("Could not read that image."); }
  };

  const onPdf = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please choose a PDF file."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("That PDF is large — please keep it under 8MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setPdfB64(String(reader.result).split(",")[1]); setPdfName(f.name); };
    reader.onerror = () => toast.error("Could not read that PDF.");
    reader.readAsDataURL(f);
  };

  const run = async () => {
    if (tab === "image" && !b64) { toast.info("Add a photo first."); return; }
    if (tab === "text" && !text.trim()) { toast.info("Paste some text first."); return; }
    if (tab === "pdf" && !pdfB64) { toast.info("Add a PDF first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/read-this", {
        image_base64: tab === "image" ? b64 : null,
        pdf_base64: tab === "pdf" ? pdfB64 : null,
        text: tab === "text" ? text.trim() : null,
        action, mode, target_language: lang,
      });
      if (data.error) toast.error(data.summary || "Could not read that.");
      else { setResult(data); loadDocs(); if (isSenior && data.summary) speak(data.summary); }
    } catch { toast.error("Could not process that."); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className={`font-head font-extrabold flex items-center gap-3 ${isSenior ? "text-4xl" : "text-3xl sm:text-4xl"}`}><ScanText className="w-8 h-8 text-primary" />Read This For Me</h1>
        <p className={`text-muted-foreground mt-2 ${isSenior ? "text-xl" : ""}`}>Snap a photo of a message, form or bill — or paste text — and I'll read it, explain it simply, pull out what you need to do, and flag anything suspicious.</p>
      </div>

      <div className="flex rounded-full bg-secondary p-1 w-full max-w-md">
        {[{ k: "image", l: "Photo", i: Upload }, { k: "pdf", l: "Document", i: FileText }, { k: "text", l: "Paste text", i: Type }].map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); setResult(null); }} data-testid={`read-tab-${t.k}`}
            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2 text-sm font-bold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <t.i className="w-4 h-4" />{t.l}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        {tab === "image" ? (
          preview ? (
            <div className="relative">
              <img src={preview} alt="preview" className="max-h-72 rounded-md mx-auto" />
              <button onClick={() => { setPreview(null); setB64(null); }} className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-background/80 border border-border"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} data-testid="read-upload"
              className="w-full rounded-lg border-2 border-dashed border-border py-12 grid place-items-center gap-2 hover:border-primary transition-colors">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <span className="font-head font-semibold">Tap to add a photo</span>
              <span className="text-xs text-muted-foreground">Your photo is used only to explain it to you.</span>
            </button>
          )
        ) : tab === "pdf" ? (
          pdfName ? (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-4" data-testid="pdf-chosen">
              <FileText className="w-8 h-8 text-primary shrink-0" />
              <span className="flex-1 font-medium truncate">{pdfName}</span>
              <button onClick={() => { setPdfB64(null); setPdfName(null); }} className="h-8 w-8 grid place-items-center rounded-full border border-border"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => pdfRef.current?.click()} data-testid="read-upload-pdf"
              className="w-full rounded-lg border-2 border-dashed border-border py-12 grid place-items-center gap-2 hover:border-primary transition-colors">
              <FileText className="w-8 h-8 text-muted-foreground" />
              <span className="font-head font-semibold">Tap to add a PDF</span>
              <span className="text-xs text-muted-foreground">Contracts, tenancy agreements, multi-page bills. Under 8MB.</span>
            </button>
          )
        ) : (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={isSenior ? 7 : 5} data-testid="read-text"
            placeholder="Paste the message, email or letter here…" className={`w-full rounded-md border border-input bg-background px-3 py-3 outline-none focus:ring-2 focus:ring-ring resize-none ${isSenior ? "text-xl" : ""}`} />
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />
        <input ref={pdfRef} type="file" accept="application/pdf" onChange={onPdf} className="hidden" />

        <div className="flex flex-wrap gap-2 mt-4">
          {ACTIONS.map((a) => (
            <button key={a.key} onClick={() => setAction(a.key)} data-testid={`read-action-${a.key}`}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold border transition-colors ${action === a.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{a.label}</button>
          ))}
        </div>
        {action === "translate" && (
          <input value={lang} onChange={(e) => setLang(e.target.value)} data-testid="read-lang" placeholder="Language" className="mt-3 rounded-md border border-input bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-ring w-48" />
        )}

        <button onClick={run} disabled={loading} data-testid="read-run" className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-bold hover:brightness-110 disabled:opacity-50 transition ${isSenior ? "py-4 text-xl" : "py-3"}`}>
          <Sparkles className={isSenior ? "w-6 h-6" : "w-5 h-5"} /> Read this for me
        </button>
      </div>

      {loading && <Loader label="Reading and explaining…" />}

      {result && (
        <div className="space-y-4 animate-rise">
          {result.caution && (
            <div className="flex items-start gap-3 rounded-lg border-2 border-destructive/50 bg-destructive/10 p-4" data-testid="read-caution">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <p className={isSenior ? "text-lg" : "text-sm"}>{result.caution}</p>
            </div>
          )}
          {result.summary && (
            <SectionCard title="In plain words" accent>
              <p className={isSenior ? "text-xl" : "text-base"}>{result.summary}</p>
              <SpeakButton text={`${result.summary}. ${result.simplified || ""}`} className="mt-3" />
            </SectionCard>
          )}
          {result.simplified && <SectionCard title="Explained simply"><p className={`leading-relaxed ${isSenior ? "text-lg" : ""}`}>{result.simplified}</p></SectionCard>}
          {result.key_points?.length > 0 && <SectionCard title="Key points" icon={BookOpen}><Bullets items={result.key_points} /></SectionCard>}
          {result.action_items?.length > 0 && <SectionCard title="What you need to do" icon={ListTodo} accent><Bullets items={result.action_items} /></SectionCard>}
          {result.important_dates?.length > 0 && <SectionCard title="Important dates" icon={CalendarClock}><Bullets items={result.important_dates} /></SectionCard>}
          {result.difficult_terms?.length > 0 && (
            <SectionCard title="Difficult words explained">
              <ul className="space-y-2">{result.difficult_terms.map((t, i) => <li key={i}><b>{t.term}</b> — <span className="text-muted-foreground">{t.meaning}</span></li>)}</ul>
            </SectionCard>
          )}
        </div>
      )}

      {docs.length > 0 && (
        <div data-testid="doc-history">
          <h2 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><History className="w-5 h-5 text-muted-foreground" />Document history</h2>
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="flex items-center gap-3 p-3">
                  <span className="text-[10px] uppercase font-bold rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">{d.kind}</span>
                  <button onClick={() => setOpenDoc(openDoc === d.id ? null : d.id)} data-testid={`doc-${d.id}`} className="flex-1 text-left text-sm truncate hover:text-primary">{d.title}</button>
                  <span className="text-xs text-muted-foreground hidden sm:block">{new Date(d.created_at).toLocaleDateString()}</span>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${openDoc === d.id ? "rotate-180" : ""}`} />
                  <button onClick={() => delDoc(d.id)} data-testid={`doc-del-${d.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                {openDoc === d.id && d.result && (
                  <div className="border-t border-border p-4 space-y-3 text-sm">
                    {d.result.summary && <p><b>Summary:</b> {d.result.summary}</p>}
                    {d.result.simplified && <p className="text-muted-foreground">{d.result.simplified}</p>}
                    {d.result.action_items?.length > 0 && <div><b>To do:</b><Bullets items={d.result.action_items} /></div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
